import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Images, Trash2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'
import { triggerRevalidation } from '@/lib/utils'
import { useUpdateTenantProfile } from '@/hooks/use-tenant-profile'
import type { SocialLinks } from '@/hooks/use-tenant-profile'
import MediaPicker from '@/components/media/media-picker'
import provincesJson from '@/data/geo/provinsi/provinsi.json'

type Tab = 'info' | 'visual' | 'social'

type WilayahEntry = { id: string; name: string }

const PROVINCES: WilayahEntry[] = Object.entries(
  provincesJson as Record<string, string>
).map(([id, name]) => ({ id, name }))

// Lazy-loaded per province — keyed by relative path for import.meta.glob
const kabupatenModules = import.meta.glob<{ default: Record<string, string> }>(
  '../../data/geo/kabupaten_kota/*.json'
)

const SOCIAL_FIELDS: { key: keyof SocialLinks; label: string; prefix: string; placeholder: string }[] = [
  { key: 'instagram', label: 'Instagram', prefix: 'instagram.com/',          placeholder: 'username'   },
  { key: 'youtube',   label: 'YouTube',   prefix: 'youtube.com/@',           placeholder: 'handle'     },
  { key: 'tiktok',    label: 'TikTok',    prefix: 'tiktok.com/@',            placeholder: 'username'   },
  { key: 'facebook',  label: 'Facebook',  prefix: 'facebook.com/',           placeholder: 'page-name'  },
  { key: 'spotify',   label: 'Spotify',   prefix: 'open.spotify.com/artist/', placeholder: 'artist-id' },
]

export default function ProfileSettingsPage() {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)
  const tenantName = useAuthStore((s) => s.currentTenant?.name ?? '')
  const updateProfile = useUpdateTenantProfile()
  const [description, setDescription] = useState('')
  const [tagline, setTagline] = useState('')
  const [socialLinks, setSocialLinks] = useState<SocialLinks>({})
  const [saved, setSaved] = useState(false)
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [logoAssetId, setLogoAssetId] = useState<string | null>(null)
  const [bannerUrl, setBannerUrl] = useState<string | null>(null)
  const [bannerAssetId, setBannerAssetId] = useState<string | null>(null)
  const [conceptPhotoUrl, setConceptPhotoUrl] = useState<string | null>(null)
  const [conceptAssetId, setConceptAssetId] = useState<string | null>(null)
  const [brandColor, setBrandColor] = useState<string>('#000000')
  const [province, setProvince] = useState<string>('')
  const [provinceCode, setProvinceCode] = useState<string>('')
  const [city, setCity] = useState<string>('')
  const [activeTab, setActiveTab] = useState<Tab>('info')
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false)
  const [bannerPickerOpen, setBannerPickerOpen] = useState(false)
  const [conceptPickerOpen, setConceptPickerOpen] = useState(false)

  const { data: tenant, isLoading } = useQuery({
    queryKey: ['tenant-profile', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenants')
        .select('tagline,social_links,logo_url,description,asset_id,banner_url,banner_asset_id,concept_photo_url,concept_asset_id,brand_color,province,city')
        .eq('id', tenantId!)
        .single()
      if (error) throw error
      return data
    },
  })

  const { data: regencies, isLoading: isLoadingRegencies } = useQuery({
    queryKey: ['wilayah-regencies', provinceCode],
    enabled: !!provinceCode,
    staleTime: Infinity,
    queryFn: async (): Promise<WilayahEntry[]> => {
      const key = `../../data/geo/kabupaten_kota/kab-${provinceCode}.json`
      const loader = kabupatenModules[key]
      if (!loader) return []
      const mod = await loader()
      return Object.entries(mod.default).map(([subCode, name]) => ({
        id: `${provinceCode}.${subCode}`,
        name,
      }))
    },
  })

  useEffect(() => {
    if (tenant) {
      setDescription(tenant.description ?? '')
      setTagline(tenant.tagline ?? '')
      setSocialLinks((tenant.social_links as SocialLinks) ?? {})
      setLogoUrl(tenant.logo_url ?? null)
      setLogoAssetId((tenant as unknown as { asset_id?: string | null }).asset_id ?? null)
      setBannerUrl(tenant.banner_url ?? null)
      setBannerAssetId(tenant.banner_asset_id ?? null)
      setConceptPhotoUrl(tenant.concept_photo_url ?? null)
      setConceptAssetId(tenant.concept_asset_id ?? null)
      setBrandColor(tenant.brand_color ?? '#000000')
      const savedProvince = (tenant as unknown as { province?: string | null }).province ?? ''
      setProvince(savedProvince)
      setProvinceCode(PROVINCES.find((p) => p.name === savedProvince)?.id ?? '')
      setCity(tenant.city ?? '')
    }
  }, [tenant])

  function handleMediaSelect(assetId: string, url: string) {
    setLogoAssetId(assetId)
    setLogoUrl(url)
    setMediaPickerOpen(false)
  }

  function handleBannerSelect(assetId: string, url: string) {
    setBannerAssetId(assetId)
    setBannerUrl(url)
    setBannerPickerOpen(false)
  }

  function handleConceptSelect(assetId: string, url: string) {
    setConceptAssetId(assetId)
    setConceptPhotoUrl(url)
    setConceptPickerOpen(false)
  }

  async function handleSave() {
    updateProfile.mutate(
      {
        logo_url: logoUrl,
        asset_id: logoAssetId,
        description: description.trim() || null,
        tagline: tagline.trim() || null,
        social_links: socialLinks,
        banner_url: bannerUrl,
        banner_asset_id: bannerAssetId,
        concept_photo_url: conceptPhotoUrl,
        concept_asset_id: conceptAssetId,
        brand_color: brandColor || null,
        province: province.trim() || null,
        city: city.trim() || null,
      },
      {
        onSuccess: async () => {
          setSaved(true)
          setTimeout(() => setSaved(false), 3000)
          await triggerRevalidation('tenant-theme')
        },
      }
    )
  }

  if (isLoading) {
    return <p className="text-muted-foreground">Memuat profil...</p>
  }

  return (
    <>
      <div className="max-w-2xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Profil Grup</h1>
          <p className="text-muted-foreground">
            Tagline dan tautan media sosial yang ditampilkan di halaman publik.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          {([
            { key: 'info',   label: 'Info' },
            { key: 'visual', label: 'Visual' },
            { key: 'social', label: 'Media Sosial' },
          ] as { key: Tab; label: string }[]).map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === key
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Tab: Info */}
        {activeTab === 'info' && (
          <div className="space-y-6">
            {/* Tagline */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <h2 className="font-semibold">Tagline</h2>
              <div className="space-y-1">
                <label className="text-sm font-medium">Tagline grup</label>
                <input
                  type="text"
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  maxLength={120}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder='contoh: "ANTI IDOL WORSHIP" atau "For the love of music"'
                />
                <p className="text-xs text-muted-foreground">{tagline.length}/120</p>
              </div>
            </div>

            {/* Description */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <h2 className="font-semibold">Deskripsi</h2>
              <div className="space-y-1">
                <label className="text-sm font-medium">Deskripsi grup</label>
                <textarea
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={300}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  placeholder="Deskripsikan grup secara singkat untuk halaman publik dan mesin pencari..."
                />
                <p className="text-xs text-muted-foreground">{description.length}/300</p>
              </div>
            </div>

            {/* Kota Basis */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <h2 className="font-semibold">Kota Basis</h2>
              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Provinsi</label>
                  <select
                    value={provinceCode}
                    onChange={(e) => {
                      const found = PROVINCES.find((p) => p.id === e.target.value)
                      setProvinceCode(e.target.value)
                      setProvince(found?.name ?? '')
                      setCity('')
                    }}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">Pilih provinsi</option>
                    {PROVINCES.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Kabupaten / Kota</label>
                  <select
                    value={city}
                    disabled={!provinceCode || isLoadingRegencies}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                  >
                    <option value="">
                      {!provinceCode
                        ? 'Pilih provinsi terlebih dahulu'
                        : isLoadingRegencies
                          ? 'Memuat kabupaten/kota...'
                          : 'Pilih kabupaten / kota'}
                    </option>
                    {regencies?.map((r) => (
                      <option key={r.id} value={r.name}>{r.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab: Visual */}
        {activeTab === 'visual' && (
          <div className="space-y-6">
            {/* Logo */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <h2 className="font-semibold">Logo Grup</h2>
              <div className="flex items-center gap-4">
                <div className="shrink-0 h-20 w-20 rounded-lg overflow-hidden bg-muted flex items-center justify-center text-2xl font-semibold text-muted-foreground">
                  {logoUrl ? (
                    <img src={logoUrl} alt="logo preview" className="h-full w-full object-contain" />
                  ) : (
                    <span>{tenantName.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div className="space-y-1.5">
                  <button
                    type="button"
                    disabled={mediaPickerOpen}
                    onClick={() => setMediaPickerOpen(true)}
                    className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Images className="h-3.5 w-3.5" />
                    {logoUrl ? 'Ganti Logo' : 'Pilih dari Media'}
                  </button>
                  {logoUrl && (
                    <button
                      type="button"
                      onClick={() => { setLogoUrl(null); setLogoAssetId(null) }}
                      className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <Trash2 className="h-3 w-3" />
                      Hapus logo
                    </button>
                  )}
                  <p className="text-xs text-muted-foreground">Pilih dari Media Library atau upload aset baru.</p>
                </div>
              </div>
            </div>

            {/* Banner / Cover */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <div>
                <h2 className="font-semibold">Banner / Cover</h2>
                <p className="text-sm text-muted-foreground">Gambar header halaman publik group. Rasio ideal 16:9.</p>
              </div>
              {bannerUrl ? (
                <img src={bannerUrl} alt="banner preview" className="w-full h-40 object-cover rounded-lg" />
              ) : (
                <div className="w-full h-40 rounded-lg bg-muted flex items-center justify-center text-muted-foreground text-sm">
                  Belum ada banner
                </div>
              )}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  disabled={bannerPickerOpen}
                  onClick={() => setBannerPickerOpen(true)}
                  className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Images className="h-3.5 w-3.5" />
                  {bannerUrl ? 'Ganti Banner' : 'Pilih dari Media'}
                </button>
                {bannerUrl && (
                  <button
                    type="button"
                    onClick={() => { setBannerUrl(null); setBannerAssetId(null) }}
                    className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <Trash2 className="h-3 w-3" />
                    Hapus banner
                  </button>
                )}
              </div>
            </div>

            {/* Foto Konsep Utama */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <div>
                <h2 className="font-semibold">Foto Konsep Utama</h2>
                <p className="text-sm text-muted-foreground">Foto formasi lengkap group sebagai visual utama.</p>
              </div>
              <div className="flex items-start gap-4">
                <div className="shrink-0 h-32 w-32 rounded-lg overflow-hidden bg-muted flex items-center justify-center text-muted-foreground text-xs">
                  {conceptPhotoUrl ? (
                    <img src={conceptPhotoUrl} alt="concept photo preview" className="h-full w-full object-cover" />
                  ) : (
                    <span>Belum ada foto</span>
                  )}
                </div>
                <div className="space-y-1.5">
                  <button
                    type="button"
                    disabled={conceptPickerOpen}
                    onClick={() => setConceptPickerOpen(true)}
                    className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Images className="h-3.5 w-3.5" />
                    {conceptPhotoUrl ? 'Ganti Foto' : 'Pilih dari Media'}
                  </button>
                  {conceptPhotoUrl && (
                    <button
                      type="button"
                      onClick={() => { setConceptPhotoUrl(null); setConceptAssetId(null) }}
                      className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <Trash2 className="h-3 w-3" />
                      Hapus foto
                    </button>
                  )}
                  <p className="text-xs text-muted-foreground">Pilih dari Media Library atau upload foto baru.</p>
                </div>
              </div>
            </div>

            {/* Warna Brand */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <div>
                <h2 className="font-semibold">Warna Brand</h2>
                <p className="text-sm text-muted-foreground">Warna identitas group yang ditampilkan di halaman publik.</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={brandColor}
                  onChange={(e) => setBrandColor(e.target.value)}
                  className="h-8 w-10 cursor-pointer rounded border border-input"
                />
                <input
                  type="text"
                  value={brandColor}
                  onChange={(e) => setBrandColor(e.target.value)}
                  className="w-32 rounded-md border border-input bg-background px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="#000000"
                  maxLength={7}
                />
                <div
                  className="h-8 w-8 rounded-md border border-border"
                  style={{ backgroundColor: brandColor }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Tab: Media Sosial */}
        {activeTab === 'social' && (
          <div className="rounded-xl border border-border bg-card p-6 space-y-4">
            <h2 className="font-semibold">Media Sosial</h2>
            <p className="text-sm text-muted-foreground">Masukkan username saja, bukan URL lengkap.</p>
            <div className="space-y-3">
              {SOCIAL_FIELDS.map(({ key, label, prefix, placeholder }) => (
                <div key={key} className="space-y-1">
                  <label className="text-sm font-medium">{label}</label>
                  <div className="flex items-center rounded-md border border-input bg-background overflow-hidden focus-within:ring-2 focus-within:ring-ring">
                    <span className="shrink-0 px-3 py-2 text-sm text-muted-foreground bg-muted/50 border-r border-input select-none">
                      {prefix}
                    </span>
                    <input
                      type="text"
                      value={socialLinks[key] ?? ''}
                      onChange={(e) =>
                        setSocialLinks((prev) => ({
                          ...prev,
                          [key]: e.target.value.replace(/^@/, '').trim() || undefined,
                        }))
                      }
                      className="flex-1 bg-transparent px-3 py-2 text-sm focus:outline-none"
                      placeholder={placeholder}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={updateProfile.isPending}
            className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {updateProfile.isPending ? 'Menyimpan...' : 'Simpan Perubahan'}
          </button>
          {saved && <p className="text-sm text-green-600">Profil berhasil disimpan!</p>}
          {updateProfile.error && (
            <p className="text-sm text-destructive">
              {(updateProfile.error as Error).message ?? 'Terjadi kesalahan.'}
            </p>
          )}
        </div>
      </div>

      <MediaPicker
        open={mediaPickerOpen}
        onClose={() => setMediaPickerOpen(false)}
        onSelect={handleMediaSelect}
      />
      <MediaPicker
        open={bannerPickerOpen}
        onClose={() => setBannerPickerOpen(false)}
        onSelect={handleBannerSelect}
      />
      <MediaPicker
        open={conceptPickerOpen}
        onClose={() => setConceptPickerOpen(false)}
        onSelect={handleConceptSelect}
      />
    </>
  )
}
